// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { Sort, invert } from '@/constants/sort';
import { useTableSort } from '@/hooks/use-table-sort';
import {
  DEFAULT_MOVEMENT_PAGE_SIZE,
  Movement,
} from '@/items/api/get-item-movement-history';
import { formatMovementType } from '@/items/utilities/format-movement-type';
import { formatNumber } from '@/utilities/number';
import {
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { Link } from '@tanstack/react-router';

type MovementSortProperty = 'movedAt';

interface MovementHistoryTableProps {
  movements: Movement[];
  totalCount: number;
  page: number;
  pageSize?: number;
  sort: Sort<MovementSortProperty>;
  isFetching?: boolean;
  onPageChange: (page: number) => void;
  onSortChange: (sort: Sort<MovementSortProperty>) => void;
}

const headerCellStyle = {
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
} as const;

/**
 * Table component for displaying bulk item movement history.
 */
export function MovementHistoryTable(props: MovementHistoryTableProps) {
  const {
    movements,
    totalCount,
    page,
    pageSize = DEFAULT_MOVEMENT_PAGE_SIZE,
    sort,
    isFetching,
    onPageChange,
    onSortChange,
  } = props;

  const { getLabelDirection, isLabelActive } =
    useTableSort<MovementSortProperty>(sort);

  const handleSortClick = () => {
    onSortChange({
      property: 'movedAt',
      direction: isLabelActive('movedAt') ? invert(sort.direction) : 'desc',
    });
  };

  if (movements.length === 0) {
    return (
      <EmptyState
        title="No movement history"
        message="This item has no recorded inventory movements"
      />
    );
  }

  return (
    <>
      {isFetching && <LinearProgress />}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellStyle}>
                <TableSortLabel
                  active={isLabelActive('movedAt')}
                  direction={getLabelDirection('movedAt')}
                  onClick={handleSortClick}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellStyle}>Type</TableCell>
              <TableCell sx={headerCellStyle}>From</TableCell>
              <TableCell sx={headerCellStyle}>To</TableCell>
              <TableCell sx={headerCellStyle}>Reason/Note</TableCell>
              <TableCell sx={headerCellStyle} align="right">
                Quantity
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {movements.map((movement) => (
              <TableRow key={movement.id}>
                <TableCell>
                  <Typography variant="body2">
                    {movement.movedAt.toFormat('MMM d, yyyy h:mm a')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatMovementType(movement.movementType)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {movement.fromLocation ? (
                    <Link
                      to="/locations/$locationKey"
                      params={{ locationKey: movement.fromLocation.key }}
                      style={{ textDecoration: 'none' }}
                    >
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ '&:hover': { textDecoration: 'underline' } }}
                      >
                        {movement.fromLocation.name}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {movement.toLocation ? (
                    <Link
                      to="/locations/$locationKey"
                      params={{ locationKey: movement.toLocation.key }}
                      style={{ textDecoration: 'none' }}
                    >
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ '&:hover': { textDecoration: 'underline' } }}
                      >
                        {movement.toLocation.name}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color={
                      movement.reasonNote ? 'text.primary' : 'text.secondary'
                    }
                  >
                    {movement.reasonNote ?? '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {formatNumber(movement.quantity)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page,
          pageSize,
          onPageChange,
        }}
        totalCount={totalCount}
        entityOptions={{ singular: 'movement', includeCount: false }}
      />
    </>
  );
}

export function MovementHistoryTableSkeleton() {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellStyle}>Date</TableCell>
            <TableCell sx={headerCellStyle}>Type</TableCell>
            <TableCell sx={headerCellStyle}>From</TableCell>
            <TableCell sx={headerCellStyle}>To</TableCell>
            <TableCell sx={headerCellStyle}>Reason/Note</TableCell>
            <TableCell sx={headerCellStyle} align="right">
              Quantity
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 5 }).map((_, index) => (
            <TableRow key={index}>
              <TableCell>
                <Skeleton variant="text" width="80%" />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="60%" />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="70%" />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="70%" />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="90%" />
              </TableCell>
              <TableCell align="right">
                <Skeleton variant="text" width="40%" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
